import React from "react";
import { Box, Typography } from "@mui/material";
import { T, MOTION } from "utils/backrrTokens";

// A ranked row whose bar sits BEHIND the label rather than beside it.
//
// This replaces an @mui/x-charts BarChart for these lists. A bar chart spends a
// fixed left margin on category labels — ours was 130px, which clipped anything
// past about sixteen characters — and spends vertical space on an axis that a
// sorted list does not need. Here the full label always fits, the row is ~26px
// instead of ~34px, and the magnitude is still encoded by length.
const BarRow = ({ label, count, max, color, indent = 0, muted = false, onClick, trailing, badge }) => {
  const pct = max > 0 ? Math.max(1.5, (count / max) * 100) : 0;
  const interactive = typeof onClick === "function";

  return (
    <Box
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: 26,
        paddingLeft: `${8 + indent * 14}px`,
        paddingRight: "8px",
        borderRadius: "4px",
        cursor: interactive ? "pointer" : "default",
        "&:hover": interactive ? { backgroundColor: T.grid } : undefined,
        "&:focus-visible": {
          outline: `2px solid ${T.accent}`,
          outlineOffset: "-2px",
        },
      }}
    >
      {/* The fill is absolutely positioned and non-interactive so it tints the
          row without affecting layout or swallowing clicks. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: 0,
          top: 2,
          bottom: 2,
          width: `${pct}%`,
          backgroundColor: color,
          opacity: muted ? 0.45 : 1,
          borderRadius: "4px",
          pointerEvents: "none",
          ...MOTION.sx,
        }}
      />
      {/* The badge sits against the label, not out by the count: it qualifies
          the name ("Pro Yearly V2"), so holding it at the far edge would read as
          a column of its own. Both live in one flexed box so the badge keeps its
          width and only the text ellipsizes. */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: muted ? 400 : 500,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={label}
          color={T.ink}
        >
          {label}
        </Typography>
        {badge}
      </Box>
      {trailing}
      <Typography
        sx={{
          position: "relative",
          fontSize: "0.78rem",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          flexShrink: 0,
        }}
        color={T.inkMuted}
      >
        {count}
      </Typography>
    </Box>
  );
};

export default BarRow;
